import { useState } from "react";
import {
  AlertTriangle,
  Check,
  Clipboard,
  CreditCard,
  KeyRound,
  Layers,
  Lock,
  Server,
  ShieldCheck,
  Wallet,
} from "lucide-react";

import { PageHead, Panel } from "@/components/bits";
import { Button } from "@/components/ui/button";

type Language = "curl" | "nodejs";
type Surface = "bearer" | "basic";

interface Endpoint {
  surface: Surface;
  group: "payments" | "treasury" | "cards";
  method: "GET" | "POST";
  path: string;
  title: string;
  description: string;
  idempotencyHeader?: boolean;
  body?: Record<string, unknown>;
  response: string;
  scope?: string;
  sensitive?: boolean;
}

const API = "https://api.bizconnectpay.com/api/v1";
const CARD_API = `${API}/card-api`;

function snippet(ep: Endpoint, lang: Language): string {
  const base = ep.surface === "basic" ? CARD_API : API;
  const url = `${base}${ep.path}`;
  const auth =
    ep.surface === "basic"
      ? "Authorization: Basic <BASE64(CLIENT_ID:CLIENT_SECRET)>"
      : "Authorization: Bearer <ACCESS_TOKEN>";
  const headers = [
    "Accept: application/json",
    auth,
    "X-Correlation-ID: <UUID>",
    ...(ep.body ? ["Content-Type: application/json"] : []),
    ...(ep.idempotencyHeader
      ? ["Idempotency-Key: <CLE_UNIQUE_PAR_OPERATION>"]
      : []),
  ];
  const json = ep.body ? JSON.stringify(ep.body, null, 2) : "";
  if (lang === "curl") {
    return [
      `curl --request ${ep.method} \\`,
      `  --url '${url}' \\`,
      ...headers.map(
        (h, i) =>
          `  --header '${h}'${i < headers.length - 1 || json ? " \\" : ""}`,
      ),
      ...(json ? [`  --data '${json.replace(/\n/g, "\n  ")}'`] : []),
    ].join("\n");
  }
  const authJs =
    ep.surface === "basic"
      ? "'Basic ' + Buffer.from(`${process.env.BCP_CLIENT_ID}:${process.env.BCP_CLIENT_SECRET}`).toString('base64')"
      : "`Bearer ${accessToken}`";
  return `// ${ep.surface === "basic" ? "Serveur marchand uniquement" : "Session utilisateur du tableau de bord"}
const response = await fetch('${url}', {
  method: '${ep.method}',
  headers: {
    'Accept': 'application/json',
    'Authorization': ${authJs},
    'X-Correlation-ID': crypto.randomUUID(),${ep.body ? "\n    'Content-Type': 'application/json'," : ""}${ep.idempotencyHeader ? "\n    'Idempotency-Key': '<CLE_UNIQUE_PAR_OPERATION>'," : ""}
  },${json ? `\n  body: JSON.stringify(${json.replace(/\n/g, "\n  ")}),` : ""}
});
const { success, data, meta } = await response.json();`;
}

const env = (data: string) => `{
  "success": true,
  "data": ${data},
  "meta": { "correlation_id": "<UUID>" }
}`;

const endpoints: Endpoint[] = [
  {
    surface: "bearer",
    group: "payments",
    method: "POST",
    path: "/payment-intents",
    title: "Dépôt (payment intent)",
    description:
      "Initie un dépôt mobile money. L'en-tête Idempotency-Key est obligatoire. La réponse 201/202 contient l'intention, l'état fournisseur et un provider_link optionnel. Suivi : GET /payment-intents/{paymentIntentId}.",
    idempotencyHeader: true,
    body: {
      amount: "5000",
      currency: "XAF",
      country: "CM",
      operation: "DEPOSIT",
      payment_method: "mtn",
      payer: { phone_number: "690000000", lang: "fr" },
      description: "ORDER-1001",
    },
    response: env(`{
    "id": "<PAYMENT_INTENT_ID>",
    "status": "<ETAT_RETOURNE_PAR_LE_SERVEUR>",
    "provider_link": "<URL_FOURNISSEUR_OPTIONNELLE>"
  }`),
  },
  {
    surface: "bearer",
    group: "payments",
    method: "POST",
    path: "/withdrawals",
    title: "Retrait",
    description:
      "Retire depuis un wallet actif vers une destination mobile money. Idempotency-Key obligatoire. Suivi : GET /withdrawals/{reference}.",
    idempotencyHeader: true,
    body: {
      wallet_id: "<WALLET_ID>",
      amount: "5000",
      currency: "XAF",
      country: "CM",
      payment_method: "mtn",
      destination: "+237690000000",
      metadata: {},
    },
    response: env(`{
    "reference": "<REFERENCE>",
    "status": "<ETAT_RETOURNE_PAR_LE_SERVEUR>"
  }`),
  },
  {
    surface: "bearer",
    group: "payments",
    method: "POST",
    path: "/wallet-transfers",
    title: "Transfert vers un utilisateur BizConnectPay",
    description:
      "Le serveur résout le matricule du destinataire ; aucun annuaire n'est exposé au navigateur. Le destinataire doit avoir un wallet actif dans le même pays et la même devise. Idempotency-Key obligatoire.",
    idempotencyHeader: true,
    body: {
      source_wallet_id: "<WALLET_ID>",
      recipient_matricule: "BCP26K7M4X2Q9R5P",
      amount: "5000",
      currency: "XAF",
      description: "Invoice 2026-18",
    },
    response: env(`{
    "reference": "<REFERENCE>",
    "source_wallet_id": "<WALLET_ID>",
    "destination_wallet_id": "<WALLET_ID>",
    "currency": "XAF",
    "amount": "5000",
    "status": "<ETAT_RETOURNE_PAR_LE_SERVEUR>"
  }`),
  },
  {
    surface: "bearer",
    group: "treasury",
    method: "POST",
    path: "/conversions",
    title: "Conversion entre wallets",
    description:
      "Convertit un montant source entre deux wallets du compte. La clé d'idempotence est transmise dans le corps. Le taux appliqué, les frais et le montant crédité sont uniquement ceux renvoyés par le serveur.",
    body: {
      source_wallet_id: "<WALLET_ID_SOURCE>",
      destination_wallet_id: "<WALLET_ID_DESTINATION>",
      source_amount: "10000",
      idempotency_key: "<CLE_UNIQUE_PAR_OPERATION>",
    },
    response: env(`{
    "applied_rate": "<TAUX_SERVEUR>",
    "fee": "<FRAIS_SERVEUR>",
    "destination_amount": "<MONTANT_SERVEUR>",
    "status": "<ETAT_RETOURNE_PAR_LE_SERVEUR>"
  }`),
  },
  {
    surface: "bearer",
    group: "treasury",
    method: "POST",
    path: "/reservations",
    title: "Réserver des fonds",
    description:
      'Bloque un montant jusqu\'à capture, libération ou expiration. Consultation : GET /reservations/{reservationId}. Capture ou libération : POST /reservations/{reservationId}/capture ou /release avec { "idempotency_key": "..." }. Aucune route de liste n\'existe.',
    body: {
      wallet_id: "<WALLET_ID>",
      operation: "MERCHANT_RESERVATION",
      operation_reference: "<CLE_UNIQUE_PAR_OPERATION>",
      amount: "2500",
      currency: "XOF",
      purpose: "Garantie commande 2026-41",
      expires_at: "2026-11-01T00:00:00Z",
    },
    response: env(`{
    "id": "<RESERVATION_ID>",
    "status": "<ETAT_RETOURNE_PAR_LE_SERVEUR>"
  }`),
  },
  {
    surface: "bearer",
    group: "cards",
    method: "POST",
    path: "/virtual-card-users",
    title: "Créer un titulaire de carte",
    description:
      "Transmet le titulaire validé au fournisseur. La réponse expose seulement la correspondance locale et la référence fournisseur, jamais les données personnelles soumises. GET /virtual-card-users/{localCustomerId} est servi en no-store et ne doit pas être mis en cache.",
    body: {
      first_name: "Miguel",
      last_name: "Tankeu",
      email: "user@example.com",
      country_code: "237",
      contact: "690000000",
      dob: "2000-08-13",
      billing_name: "Miguel Tankeu",
      is_business: false,
      business_name: null,
      billing_address: "Bonamoussadi",
      billing_city: "Douala",
      billing_country: "CM",
      billing_state: "Littoral",
      billing_postal_code: "237",
      id_number: "<NUMERO_PIECE>",
    },
    response: env(`{
    "id": "<LOCAL_CUSTOMER_ID>",
    "provider_reference": "<REFERENCE_FOURNISSEUR>"
  }`),
  },
  {
    surface: "bearer",
    group: "cards",
    method: "GET",
    path: "/virtual-cards/quote",
    title: "Devis d'émission de carte",
    description:
      "Sélectionne automatiquement un wallet actif ayant assez de fonds, puis retourne le rang de la carte, les frais en USD, le taux configuré (1 USD = 690 XAF, puis conversion administrateur vers la devise du wallet) et le débit exact. Affichez toujours le devis renvoyé : ne le calculez jamais côté client.",
    response: env(`{
    "sequence": "<RANG_SERVEUR>",
    "fee_usd": "<FRAIS_SERVEUR>",
    "rate": "<TAUX_CONFIGURE>",
    "debit_amount": "<DEBIT_SERVEUR>",
    "debit_currency": "XAF",
    "selected_wallet": {
      "id": "<WALLET_ID_CHOISI_PAR_LE_SERVEUR>",
      "currency": "XAF",
      "available_balance": "<SOLDE_DISPONIBLE>"
    }
  }`),
  },
  {
    surface: "bearer",
    group: "cards",
    method: "POST",
    path: "/virtual-cards/{localCardId}/operations",
    title: "Opération sur une carte",
    description:
      "Actions prises en charge : topup, withdraw, enabled, disabled, terminate. L'émission (POST /virtual-cards) réserve les frais du devis, les capture en cas de succès et les libère si le fournisseur échoue ; une clé d'idempotence réutilisée ne peut ni émettre ni facturer une seconde carte.",
    body: { action: "topup", amount: "20.00" },
    response: env(`{
    "status": "<ETAT_RETOURNE_PAR_LE_SERVEUR>"
  }`),
  },
  {
    surface: "basic",
    group: "cards",
    method: "GET",
    path: "/users",
    scope: "virtual_cards.users.read",
    title: "Lister les titulaires (application)",
    description:
      "Également GET /users/{localCustomerId}. Références locales sûres uniquement.",
    response: env(`[
    { "id": "<LOCAL_CUSTOMER_ID>", "provider_reference": "<REFERENCE_FOURNISSEUR>" }
  ]`),
  },
  {
    surface: "basic",
    group: "cards",
    method: "POST",
    path: "/users",
    scope: "virtual_cards.users.write",
    title: "Créer un titulaire (application)",
    description: "Même corps que la route Bearer /virtual-card-users.",
    body: {
      first_name: "Miguel",
      last_name: "Tankeu",
      email: "user@example.com",
      country_code: "237",
      contact: "690000000",
      dob: "2000-08-13",
      billing_country: "CM",
      id_number: "<NUMERO_PIECE>",
    },
    response: env(
      `{ "id": "<LOCAL_CUSTOMER_ID>", "provider_reference": "<REFERENCE_FOURNISSEUR>" }`,
    ),
  },
  {
    surface: "basic",
    group: "cards",
    method: "GET",
    path: "/cards",
    scope: "virtual_cards.cards.read",
    title: "Lister les cartes et devis (application)",
    description:
      "Également GET /cards/quote. Correspondances de cartes sûres, sans données sensibles.",
    response: env(`[
    { "id": "<LOCAL_CARD_ID>", "status": "<ETAT_SERVEUR>" }
  ]`),
  },
  {
    surface: "basic",
    group: "cards",
    method: "POST",
    path: "/cards",
    scope: "virtual_cards.cards.write",
    title: "Émettre une carte (application)",
    description:
      "Même corps que la route Bearer POST /virtual-cards. Ne transmettez pas wallet_id : le serveur choisit un wallet actif du marchand avec un solde suffisant. Les secrets de carte ne sont ni retournés à la création ni stockés.",
    body: {
      customer_id: "<LOCAL_CUSTOMER_ID>",
      idempotency_key: "<CLE_UNIQUE_PAR_OPERATION>",
      initial_balance: "2.00",
      category: "PERSONAL",
      grade: "STANDARD",
    },
    response: env(`{
    "card": { "id": "<LOCAL_CARD_ID>" },
    "quote": "<DEVIS_SERVEUR>",
    "reservation_status": "<ETAT_SERVEUR>"
  }`),
  },
  {
    surface: "basic",
    group: "cards",
    method: "POST",
    path: "/cards/{localCardId}/operations",
    scope: "virtual_cards.cards.manage",
    title: "Gérer une carte (application)",
    description: "Actions : topup, withdraw, enabled, disabled, terminate.",
    body: { action: "disabled" },
    response: env(`{ "status": "<ETAT_RETOURNE_PAR_LE_SERVEUR>" }`),
  },
  {
    surface: "basic",
    group: "cards",
    method: "GET",
    path: "/cards/{localCardId}",
    scope: "virtual_cards.cards.sensitive.read",
    sensitive: true,
    title: "Détail sensible d'une carte (application)",
    description:
      "Peut exposer PAN, CVV, PIN, données du titulaire et transactions du fournisseur. Réponse en Cache-Control: no-store. Ce scope dédié ne doit jamais être accordé par défaut.",
    response: env(`{
    "card": {
      "card_id": "<PROVIDER_CARD_ID>",
      "masked_pan": "428852******3108",
      "currency": "USD",
      "status": "<ETAT_FOURNISSEUR>"
    },
    "transactions": []
  }`),
  },
];

const SCOPES: [string, string, boolean?][] = [
  ["virtual_cards.users.read", "GET /users et GET /users/{localCustomerId}"],
  ["virtual_cards.users.write", "POST /users"],
  ["virtual_cards.cards.read", "GET /cards et GET /cards/quote"],
  [
    "virtual_cards.cards.sensitive.read",
    "GET /cards/{localCardId} (PAN, CVV, PIN)",
    true,
  ],
  ["virtual_cards.cards.write", "POST /cards"],
  ["virtual_cards.cards.manage", "POST /cards/{localCardId}/operations"],
];

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="relative overflow-hidden rounded-lg bg-[#07142f] text-slate-100">
      <Button
        variant="ghost"
        size="sm"
        onClick={copy}
        aria-label="Copier l'exemple"
        className="absolute right-2 top-2 h-8 gap-1.5 text-slate-300 hover:bg-white/10 hover:text-white"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5" />
        ) : (
          <Clipboard className="h-3.5 w-3.5" />
        )}
        {copied ? "Copié" : "Copier"}
      </Button>
      <pre className="overflow-x-auto p-4 pr-24 font-mono text-xs leading-6">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function ApiReferencePage() {
  const [lang, setLang] = useState<Language>("curl");
  const [filter, setFilter] = useState<string>("all");

  const filtered = endpoints.filter(
    (ep) => filter === "all" || ep.surface === filter || ep.group === filter,
  );

  return (
    <>
      <PageHead
        title="Documentation Développeur & API"
        sub="Deux surfaces distinctes : la session Bearer du tableau de bord et les identifiants Basic de vos applications serveur."
      />

      <div className="space-y-6">
        <Panel>
          <div className="flex items-center gap-2 text-primary">
            <Server className="h-5 w-5" />
            <h2 className="text-lg font-extrabold">
              Surfaces d'authentification
            </h2>
          </div>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div
              className="rounded-xl border bg-muted/30 p-4"
              data-testid="doc-surface-bearer"
            >
              <span className="text-xs font-bold uppercase tracking-wider text-primary">
                1. Tableau de bord (Bearer)
              </span>
              <p className="mt-1 break-all font-mono text-sm font-bold text-[#0B1F4B]">
                {API}
              </p>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                <code className="font-mono">
                  Authorization: Bearer &lt;ACCESS_TOKEN&gt;
                </code>{" "}
                issu de la connexion utilisateur. Couvre dépôts, retraits,
                transferts, conversions, réservations et cartes virtuelles du
                compte connecté. L'identité, le compte, les rôles et les pays
                sont dérivés du jeton signé : n'envoyez jamais d'en-tête
                d'identité.
              </p>
            </div>
            <div
              className="rounded-xl border bg-muted/30 p-4"
              data-testid="doc-surface-basic"
            >
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">
                2. Applications (Basic)
              </span>
              <p className="mt-1 break-all font-mono text-sm font-bold text-[#0B1F4B]">
                {CARD_API}
              </p>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                <code className="font-mono">
                  Authorization: Basic base64(client_id:client_secret)
                </code>
                . Identifiants générés dans Applications, affichés une seule
                fois. À utiliser exclusivement depuis vos serveurs, jamais dans
                un navigateur ni une application mobile.
              </p>
            </div>
          </div>
          <p className="mt-4 flex items-start gap-2 rounded-lg bg-accent/60 p-3 text-xs text-muted-foreground">
            <Layers className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            Toutes les requêtes envoient{" "}
            <code className="font-mono">Accept: application/json</code> et un
            UUID dans <code className="font-mono">X-Correlation-ID</code>. Les
            montants sont des chaînes décimales positives. Les réponses
            utilisent l'enveloppe{" "}
            <code className="font-mono">{"{ success, data, meta }"}</code> ; les
            erreurs renvoient <code className="font-mono">data.error.code</code>{" "}
            et <code className="font-mono">data.error.message</code>.
          </p>
        </Panel>

        <Panel>
          <div className="flex items-center gap-2 text-primary">
            <KeyRound className="h-5 w-5" />
            <h2 className="text-lg font-extrabold">
              Scopes des applications (card-api)
            </h2>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Accordez uniquement les scopes nécessaires.
          </p>
          <ul className="mt-4 divide-y rounded-xl border">
            {SCOPES.map(([scope, routes, sensitive]) => (
              <li
                key={scope}
                className="flex flex-wrap items-center gap-3 px-4 py-2.5"
              >
                <code className="font-mono text-xs font-bold">{scope}</code>
                <span className="flex-1 text-xs text-muted-foreground">
                  {routes}
                </span>
                {sensitive && (
                  <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 ring-1 ring-rose-600/15">
                    Jamais par défaut
                  </span>
                )}
              </li>
            ))}
          </ul>
          <div
            className="mt-4 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50/70 p-4 text-xs leading-relaxed text-rose-900"
            data-testid="doc-sensitive-guidance"
          >
            <Lock className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-bold">Données de carte sensibles</p>
              <p className="mt-1">
                Le scope{" "}
                <code className="font-mono">
                  virtual_cards.cards.sensitive.read
                </code>{" "}
                est dédié et doit faire l'objet d'une demande explicite. Les
                réponses sont en{" "}
                <code className="font-mono">Cache-Control: no-store</code>.
                N'affichez PAN, CVV ou PIN qu'après une action explicite de
                l'utilisateur, effacez-les lorsque la page perd le focus,
                excluez-les des outils d'analyse et de suivi d'erreurs, et ne
                les stockez jamais (stockage navigateur, journaux, base de
                données). BizConnectPay ne stocke ni PAN, ni CVV, ni PIN, ni
                solde fournisseur.
              </p>
            </div>
          </div>
        </Panel>

        <Panel>
          <div className="flex items-center gap-2 text-primary">
            <ShieldCheck className="h-5 w-5" />
            <h2 className="text-lg font-extrabold">Idempotence et états</h2>
          </div>
          <ul className="mt-3 list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
            <li>
              Dépôts, retraits, transferts et remboursements : en-tête{" "}
              <code className="font-mono">Idempotency-Key</code>.
            </li>
            <li>
              Conversions, réservations (création, capture, libération) et
              émission de cartes : champ{" "}
              <code className="font-mono">idempotency_key</code> dans le corps.
            </li>
            <li>
              Réutilisez la même clé pour réessayer une requête dont l'issue est
              incertaine ; générez-en une nouvelle pour une nouvelle opération.
            </li>
            <li>
              Les exemples de réponse ci-dessous sont schématiques : statuts,
              frais, taux et débits sont toujours ceux renvoyés par le serveur.
            </li>
          </ul>
        </Panel>

        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div
            className="flex flex-wrap gap-2"
            role="tablist"
            aria-label="Langage des exemples"
          >
            {(["curl", "nodejs"] as Language[]).map((l) => (
              <Button
                key={l}
                size="sm"
                variant={lang === l ? "default" : "outline"}
                onClick={() => setLang(l)}
                className="text-xs font-semibold"
                aria-pressed={lang === l}
              >
                {l === "curl" ? "cURL" : "Node.js"}
              </Button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { id: "all", label: "Tout", icon: Layers },
              { id: "bearer", label: "Bearer", icon: KeyRound },
              { id: "basic", label: "Basic card-api", icon: Lock },
              { id: "payments", label: "Paiements", icon: Wallet },
              { id: "treasury", label: "Trésorerie", icon: Layers },
              { id: "cards", label: "Cartes", icon: CreditCard },
            ].map((cat) => (
              <Button
                key={cat.id}
                variant={filter === cat.id ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter(cat.id)}
                className="gap-1.5 text-xs font-semibold"
                aria-pressed={filter === cat.id}
              >
                <cat.icon className="h-3.5 w-3.5" />
                {cat.label}
              </Button>
            ))}
          </div>
        </div>

        {filtered.map((ep) => (
          <Panel key={`${ep.surface}-${ep.method}-${ep.path}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span
                className={
                  ep.method === "GET"
                    ? "rounded bg-blue-100 px-2 py-0.5 text-xs font-black text-blue-700"
                    : "rounded bg-emerald-100 px-2 py-0.5 text-xs font-black text-emerald-700"
                }
              >
                {ep.method}
              </span>
              <code className="break-all text-sm font-bold text-primary">
                {ep.surface === "basic" ? CARD_API : API}
                {ep.path}
              </code>
              <span
                className={
                  ep.surface === "basic"
                    ? "rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 ring-1 ring-emerald-600/20"
                    : "rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-primary/20"
                }
              >
                {ep.surface === "basic"
                  ? "Basic application"
                  : "Bearer tableau de bord"}
              </span>
              {ep.scope && (
                <code className="rounded bg-muted px-1.5 py-0.5 text-[11px]">
                  {ep.scope}
                </code>
              )}
            </div>
            <h3 className="mt-3 text-lg font-extrabold">{ep.title}</h3>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">
              {ep.description}
            </p>
            {ep.sensitive && (
              <p className="mt-2 flex items-center gap-2 text-xs font-semibold text-rose-700">
                <AlertTriangle className="h-4 w-4" /> Scope sensible : jamais
                accordé par défaut, réponse no-store, révélation explicite
                uniquement.
              </p>
            )}
            <div className="mt-4 grid gap-4 xl:grid-cols-2">
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Exemple de requête
                </p>
                <CodeBlock code={snippet(ep, lang)} />
              </div>
              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Forme de réponse (schématique)
                </p>
                <CodeBlock code={ep.response} />
              </div>
            </div>
          </Panel>
        ))}
      </div>
    </>
  );
}
